import React, { useRef, useState, useEffect } from "react";
import { Editor } from "@tiptap/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ImagesAPI } from "../../services/api";
import {
  faBold,
  faItalic,
  faUnderline,
  faStrikethrough,
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
  faAlignJustify,
  faListUl,
  faListOl,
  faHighlighter,
  faTable,
  faImage,
  faLink,
  faCheck,
  faTableRows,
  faTableColumns,
  faTableCells,
  faTextSize,
  faFontCase,
  faBan,
  faRectangleWide,
  faRectangleVertical,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { DropDown } from "../ui/dropdown";
import { Menu } from "@headlessui/react";
import ColorPicker from "../ui/color-picker";
import Glassmorphism from "../Glassmorphism";
import FormInput from "../ui/form-input";
import {
  selectionHasUnderline,
  toggleReportTextUnderline,
} from "@/components/common/report-builder/utils/reportTextFormatting";

interface DocumentRichTextToolbarProps {
  editor: Editor | null;
  onFileUpload?: (file: File) => Promise<string>;
  doc?: {
    id: string;
    [key: string]: any;
  };
  landscapeMode?: boolean;
  onLandscapeModeChange?: (value: boolean) => void;
  /** No card background — for embedded editors (e.g. artifacts). Adds horizontal padding. */
  embedded?: boolean;
}

const DocumentRichTextToolbar: React.FC<DocumentRichTextToolbarProps> = ({
  editor,
  landscapeMode = false,
  onLandscapeModeChange,
  embedded = false,
}) => {
  const [highlightColor, setHighlightColor] = useState<string>("#fef08a");
  const [currentImageWidth, setCurrentImageWidth] = useState<string>("");
  const [isImageSelected, setIsImageSelected] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editor) return;

    // Function to check and update image selection state
    const updateImageSelectionState = () => {
      const imageIsActive = editor.isActive("resizableImage");
      setIsImageSelected(imageIsActive);

      if (imageIsActive) {
        const width = editor.getAttributes("resizableImage").width;
        setCurrentImageWidth(width ? String(width) : "");
      } else {
        setCurrentImageWidth("");
      }
    };

    // Update immediately
    updateImageSelectionState();

    // Add event listener for selection updates
    const onSelectionUpdate = () => {
      updateImageSelectionState();
    };

    // Listen to selection and transaction changes
    editor.on("selectionUpdate", onSelectionUpdate);
    editor.on("transaction", onSelectionUpdate);

    // Cleanup
    return () => {
      editor.off("selectionUpdate", onSelectionUpdate);
      editor.off("transaction", onSelectionUpdate);
    };
  }, [editor]);

  if (!editor) {
    return null;
  }

  const isActive = (
    name: string | Record<string, any>,
    options?: Record<string, any>
  ) => {
    if (typeof name === "string") {
      return editor.isActive(name, options);
    }

    // Handle when first parameter is an object (e.g., { textAlign: 'left' })
    return editor.isActive(name);
  };

  const getAttributes = (name: string) => {
    return editor.getAttributes(name);
  };

  const handleKeyDownForClick = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.currentTarget.click();
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (file.size > 5 * 1024 * 1024) {
      // 5MB limit
      alert("File size exceeds 5MB limit");
      return;
    }

    try {
      // Show loading state in editor
      const { from } = editor.state.selection;
      editor
        .chain()
        .focus()
        .insertContent({
          type: "paragraph",
          content: [{ type: "text", text: "Uploading image..." }],
        })
        .run();

      // Upload the image
      const response = await ImagesAPI.uploadImage(file);

      // Get the first image from the response data array
      const uploadedImage = response.data[0];

      if (uploadedImage && uploadedImage.url) {
        // Remove the loading text and insert the image
        editor.commands.deleteRange({ from, to: editor.state.selection.from });
        editor.chain().focus().setImage({ src: uploadedImage.url }).run();
      }
    } catch (error) {
      console.error("Upload failed:", error);
      // Remove loading text and show error
      editor.commands.deleteRange({
        from: editor.state.selection.from - 15,
        to: editor.state.selection.from,
      });
      editor.chain().focus().insertContent("Failed to upload image").run();
    }

    event.target.value = ""; // Clear the input
  };

  // Editor commands
  const toggleBold = () => editor.chain().focus().toggleBold().run();
  const toggleItalic = () => editor.chain().focus().toggleItalic().run();
  const toggleUnderline = () => toggleReportTextUnderline(editor);
  const toggleStrike = () => editor.chain().focus().toggleStrike().run();
  const toggleBulletList = () =>
    editor.chain().focus().toggleBulletList().run();
  const toggleOrderedList = () =>
    editor.chain().focus().toggleOrderedList().run();
  const toggleHighlight = (color: string) => {
    setHighlightColor(color);
    // Make sure the editor is focused before applying the highlight
    if (!editor.isFocused) {
      editor.commands.focus();
    }
    // Then apply the highlight
    editor.chain().toggleHighlight({ color }).run();
  };
  const removeHighlight = () => {
    // Make sure the editor is focused before removing the highlight
    if (!editor.isFocused) {
      editor.commands.focus();
    }
    editor.chain().unsetHighlight().run();
  };
  const setTextAlign = (align: "left" | "center" | "right" | "justify") =>
    editor.chain().focus().setTextAlign(align).run();

  // Use custom attributes for text style since TextStyle extension might not handle classes directly
  const setTextSize = (size: string) => {
    // Set an attribute that our extension can handle
    editor
      .chain()
      .focus()
      .extendMarkRange("textStyle")
      .setMark("textStyle", { class: size })
      .run();
  };

  const setFontClass = (fontClass: string) => {
    // Set an attribute that our extension can handle
    editor
      .chain()
      .focus()
      .extendMarkRange("textStyle")
      .setMark("textStyle", { class: fontClass })
      .run();
  };

  const unsetLink = () => editor.chain().focus().unsetLink().run();
  const addImage = () => fileInputRef.current?.click();

  // Table commands
  const insertTable = () =>
    editor
      .chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run();
  const deleteTable = () => editor.chain().focus().deleteTable().run();
  const addRowAfter = () => editor.chain().focus().addRowAfter().run();
  const addRowBefore = () => editor.chain().focus().addRowBefore().run();
  const deleteRow = () => editor.chain().focus().deleteRow().run();
  const addColumnAfter = () => editor.chain().focus().addColumnAfter().run();
  const addColumnBefore = () => editor.chain().focus().addColumnBefore().run();
  const deleteColumn = () => editor.chain().focus().deleteColumn().run();
  const mergeCells = () => editor.chain().focus().mergeCells().run();
  const splitCell = () => editor.chain().focus().splitCell().run();
  const toggleHeaderCell = () =>
    editor.chain().focus().toggleHeaderCell().run();
  const toggleHeaderColumn = () =>
    editor.chain().focus().toggleHeaderColumn().run();
  const toggleHeaderRow = () => editor.chain().focus().toggleHeaderRow().run();
  const setCellBackground = (color: string) =>
    editor.chain().focus().setCellAttribute("backgroundColor", color).run();
  const setCellBorderColor = (color: string) =>
    editor.chain().focus().setCellAttribute("borderColor", color).run();

  // CSS classes for active/inactive buttons
  const getButtonClass = (active: boolean) => {
    if (embedded) {
      return active
        ? "bg-indigo-100 text-indigo-900 hover:bg-indigo-200 dark:bg-indigo-400/25 dark:text-white dark:hover:bg-indigo-400/35"
        : "bg-transparent text-slate-800 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white";
    }
    return active
      ? "bg-indigo-500/20 text-slate-900 hover:bg-indigo-500/30 dark:bg-indigo-400/25 dark:text-white dark:hover:bg-indigo-400/35"
      : "bg-transparent text-slate-600 hover:bg-slate-950/[0.06] hover:text-slate-900 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white";
  };

  const getToolbarIconClass = () =>
    embedded
      ? "text-slate-800 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white"
      : "text-slate-600 hover:bg-slate-950/[0.06] hover:text-slate-900 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white";

  return (
    <div className="z-20 flex justify-center w-full">
      <div
        className={
          embedded
            ? "flex w-full items-center justify-between gap-2 px-2 py-1"
            : "flex w-full items-center justify-between gap-2 rounded bg-white py-1.5 dark:bg-gray-800"
        }
      >
        <div className="flex flex-grow items-center gap-1.5">
          {/* Text formatting */}
          <div className="flex items-center gap-0.5">
            <button
              onClick={toggleBold}
              className={`flex h-7 w-7 flex-none items-center justify-center rounded font-semibold leading-[0px] ${
                getButtonClass(isActive("bold"))
              }`}
            >
              <FontAwesomeIcon icon={faBold} />
            </button>
            <button
              onClick={toggleItalic}
              className={`flex h-7 w-7 flex-none items-center justify-center rounded italic ${
                getButtonClass(isActive("italic"))
              }`}
            >
              <FontAwesomeIcon icon={faItalic} />
            </button>
            <button
              onClick={toggleUnderline}
              className={`flex h-7 w-7 flex-none items-center justify-center rounded ${
                getButtonClass(selectionHasUnderline(editor))
              }`}
            >
              <FontAwesomeIcon icon={faUnderline} />
            </button>
            <button
              onClick={toggleStrike}
              className={`flex h-7 w-7 flex-none items-center justify-center rounded ${
                getButtonClass(isActive("strike"))
              }`}
            >
              <FontAwesomeIcon icon={faStrikethrough} />
            </button>
          </div>

          <div className="w-px h-5 bg-slate-200 dark:bg-white/15"></div>

          {/* Text size and font family dropdowns */}
          <div className="flex items-center gap-0.5">
            <DropDown
              label={
                <div className={`flex h-7 w-7 flex-none items-center justify-center rounded-md leading-[0px] ${getToolbarIconClass()}`}>
                  <FontAwesomeIcon icon={faTextSize} />
                </div>
              }
              placement="bottom"
              width="w-44"
              className="py-1"
            >
              <Menu.Item>
                {({ active }) => (
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    onClick={() => setTextSize("text-xs leading-3")}
                    className={`flex items-center space-x-2 py-2 px-3 cursor-pointer rounded ${
                      active ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="text-xs leading-none text-gray-600">
                      <FontAwesomeIcon icon={faTextSize} />
                    </div>
                    <div className="flex-grow">X-Small</div>
                    {(getAttributes("textStyle").class ===
                      "text-xs leading-3" ||
                      !getAttributes("textStyle").class) && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-blue-600"
                      />
                    )}
                  </div>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    onClick={() => setTextSize("text-sm leading-4")}
                    className={`flex items-center space-x-2 py-2 px-3 cursor-pointer rounded ${
                      active ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="text-sm leading-none text-gray-600">
                      <FontAwesomeIcon icon={faTextSize} />
                    </div>
                    <div className="flex-grow">Small</div>
                    {getAttributes("textStyle").class ===
                      "text-sm leading-4" && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-blue-600"
                      />
                    )}
                  </div>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    onClick={() => setTextSize("text-base leading-5")}
                    className={`flex items-center space-x-2 py-2 px-3 cursor-pointer rounded ${
                      active ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="text-base leading-none text-gray-600">
                      <FontAwesomeIcon icon={faTextSize} />
                    </div>
                    <div className="flex-grow">Default</div>
                    {getAttributes("textStyle").class ===
                      "text-base leading-5" && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-blue-600"
                      />
                    )}
                  </div>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    onClick={() => setTextSize("text-lg leading-6")}
                    className={`flex items-center space-x-2 py-2 px-3 cursor-pointer rounded ${
                      active ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="text-lg leading-none text-gray-600">
                      <FontAwesomeIcon icon={faTextSize} />
                    </div>
                    <div className="flex-grow">Large</div>
                    {getAttributes("textStyle").class ===
                      "text-lg leading-6" && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-blue-600"
                      />
                    )}
                  </div>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    onClick={() => setTextSize("text-xl leading-7")}
                    className={`flex items-center space-x-2 py-2 px-3 cursor-pointer rounded ${
                      active ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="text-xl leading-none text-gray-600">
                      <FontAwesomeIcon icon={faTextSize} />
                    </div>
                    <div className="flex-grow">X-Large</div>
                    {getAttributes("textStyle").class ===
                      "text-xl leading-7" && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-blue-600"
                      />
                    )}
                  </div>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    onClick={() => setTextSize("text-2xl leading-8")}
                    className={`flex items-center space-x-2 py-2 px-3 cursor-pointer rounded ${
                      active ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="text-2xl leading-none text-gray-600">
                      <FontAwesomeIcon icon={faTextSize} />
                    </div>
                    <div className="flex-grow">XX-Large</div>
                    {getAttributes("textStyle").class ===
                      "text-2xl leading-8" && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-blue-600"
                      />
                    )}
                  </div>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    onClick={() => setTextSize("text-3xl leading-9")}
                    className={`flex items-center space-x-2 py-2 px-3 cursor-pointer rounded ${
                      active ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="text-3xl leading-none text-gray-600">
                      <FontAwesomeIcon icon={faTextSize} />
                    </div>
                    <div className="flex-grow">XXX-Large</div>
                    {getAttributes("textStyle").class ===
                      "text-6xl leading-9" && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-blue-600"
                      />
                    )}
                  </div>
                )}
              </Menu.Item>
            </DropDown>

            <DropDown
              label={
                <div className={`flex h-7 w-7 flex-none items-center justify-center rounded-md leading-[0px] ${getToolbarIconClass()}`}>
                  <FontAwesomeIcon icon={faFontCase} />
                </div>
              }
              placement="bottom"
              width="w-44"
              className="py-1"
            >
              <Menu.Item>
                {({ active }) => (
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    onClick={() => setFontClass("font-sans")}
                    className={`flex items-center space-x-2 py-2 px-3 cursor-pointer rounded ${
                      active ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="font-sans text-xl leading-none">Aa</div>
                    <div className="flex-grow">Default</div>
                    {(getAttributes("textStyle").class === "font-sans" ||
                      !getAttributes("textStyle").class) && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-blue-600"
                      />
                    )}
                  </div>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    onClick={() => setFontClass("font-serif")}
                    className={`flex items-center space-x-2 py-2 px-3 cursor-pointer rounded ${
                      active ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="font-serif text-xl leading-none">Aa</div>
                    <div className="flex-grow">Serif</div>
                    {getAttributes("textStyle").class === "font-serif" && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-blue-600"
                      />
                    )}
                  </div>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    onClick={() => setFontClass("font-mono")}
                    className={`flex items-center space-x-2 py-2 px-3 cursor-pointer rounded ${
                      active ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="font-mono text-xl leading-none">Aa</div>
                    <div className="flex-grow">Mono</div>
                    {getAttributes("textStyle").class === "font-mono" && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-blue-600"
                      />
                    )}
                  </div>
                )}
              </Menu.Item>
            </DropDown>
          </div>

          <div className="w-px h-5 bg-slate-200 dark:bg-white/15"></div>

          {/* Text alignment */}
          <div className="flex items-center gap-0.5">
            <button
              onClick={() => setTextAlign("left")}
              className={`flex h-7 w-7 flex-none items-center justify-center rounded font-semibold leading-[0px] ${
                getButtonClass(isActive({ textAlign: "left" }))
              }`}
            >
              <FontAwesomeIcon icon={faAlignLeft} />
            </button>
            <button
              onClick={() => setTextAlign("center")}
              className={`flex h-7 w-7 flex-none items-center justify-center rounded font-semibold leading-[0px] ${
                getButtonClass(isActive({ textAlign: "center" }))
              }`}
            >
              <FontAwesomeIcon icon={faAlignCenter} />
            </button>
            <button
              onClick={() => setTextAlign("right")}
              className={`flex h-7 w-7 flex-none items-center justify-center rounded font-semibold leading-[0px] ${
                getButtonClass(isActive({ textAlign: "right" }))
              }`}
            >
              <FontAwesomeIcon icon={faAlignRight} />
            </button>
            <button
              onClick={() => setTextAlign("justify")}
              className={`flex h-7 w-7 flex-none items-center justify-center rounded font-semibold leading-[0px] ${
                getButtonClass(isActive({ textAlign: "justify" }))
              }`}
            >
              <FontAwesomeIcon icon={faAlignJustify} />
            </button>
          </div>

          <div className="w-px h-5 bg-slate-200 dark:bg-white/15"></div>

          {/* Lists */}
          <div className="flex items-center gap-0.5">
            <button
              onClick={toggleBulletList}
              className={`flex h-7 w-7 flex-none items-center justify-center rounded font-semibold leading-[0px] ${
                getButtonClass(isActive("bulletList"))
              }`}
            >
              <FontAwesomeIcon icon={faListUl} />
            </button>
            <button
              onClick={toggleOrderedList}
              className={`flex h-7 w-7 flex-none items-center justify-center rounded font-semibold leading-[0px] ${
                getButtonClass(isActive("orderedList"))
              }`}
            >
              <FontAwesomeIcon icon={faListOl} />
            </button>
          </div>

          <div className="w-px h-5 bg-slate-200 dark:bg-white/15"></div>

          {/* Highlight */}
          <div className="flex items-center gap-0.5">
            <DropDown
              label={
                <div
                  className={`flex h-7 w-7 flex-none items-center justify-center rounded font-semibold leading-[0px] ${
                    getButtonClass(isActive("highlight"))
                  }`}
                >
                  <FontAwesomeIcon icon={faHighlighter} />
                </div>
              }
              placement="bottom"
              width="w-44"
              className="py-1"
            >
              <div className="px-3 py-2 bg-white dark:bg-gray-800">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Highlight Color
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {/* Preset colors */}
                  <div
                    className={`h-6 w-6 rounded-full cursor-pointer ring-offset-white dark:ring-offset-gray-800 ${
                      highlightColor === "#fef08a"
                        ? "ring-2 ring-indigo-500 ring-offset-1"
                        : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    style={{ backgroundColor: "#fef08a" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleHighlight("#fef08a");
                    }}
                  />
                  <div
                    className={`h-6 w-6 rounded-full cursor-pointer ring-offset-white dark:ring-offset-gray-800 ${
                      highlightColor === "#bbf7d0"
                        ? "ring-2 ring-indigo-500 ring-offset-1"
                        : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    style={{ backgroundColor: "#bbf7d0" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleHighlight("#bbf7d0");
                    }}
                  />
                  <div
                    className={`h-6 w-6 rounded-full cursor-pointer ring-offset-white dark:ring-offset-gray-800 ${
                      highlightColor === "#bae6fd"
                        ? "ring-2 ring-indigo-500 ring-offset-1"
                        : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    style={{ backgroundColor: "#bae6fd" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleHighlight("#bae6fd");
                    }}
                  />
                  <div
                    className={`h-6 w-6 rounded-full cursor-pointer ring-offset-white dark:ring-offset-gray-800 ${
                      highlightColor === "#ddd6fe"
                        ? "ring-2 ring-indigo-500 ring-offset-1"
                        : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    style={{ backgroundColor: "#ddd6fe" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleHighlight("#ddd6fe");
                    }}
                  />
                  <div
                    className={`h-6 w-6 rounded-full cursor-pointer ring-offset-white dark:ring-offset-gray-800 ${
                      highlightColor === "#fecaca"
                        ? "ring-2 ring-indigo-500 ring-offset-1"
                        : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    style={{ backgroundColor: "#fecaca" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleHighlight("#fecaca");
                    }}
                  />
                  <div
                    className={`h-6 w-6 rounded-full cursor-pointer ring-offset-white dark:ring-offset-gray-800 ${
                      highlightColor === "#f5f5f5"
                        ? "ring-2 ring-indigo-500 ring-offset-1"
                        : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    style={{ backgroundColor: "#f5f5f5" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleHighlight("#f5f5f5");
                    }}
                  />
                  <div
                    className={`h-6 w-6 rounded-full cursor-pointer ring-offset-white dark:ring-offset-gray-800 ${
                      highlightColor === "#e5e7eb"
                        ? "ring-2 ring-indigo-500 ring-offset-1"
                        : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    style={{ backgroundColor: "#e5e7eb" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleHighlight("#e5e7eb");
                    }}
                  />

                  {/* Custom color picker trigger */}
                  <div
                    className={`h-6 w-6 rounded-full cursor-pointer relative overflow-hidden ring-offset-white dark:ring-offset-gray-800 ${
                      ![
                        "#fef08a",
                        "#bbf7d0",
                        "#bae6fd",
                        "#ddd6fe",
                        "#fecaca",
                        "#f5f5f5",
                        "#e5e7eb",
                      ].includes(highlightColor)
                        ? "ring-2 ring-indigo-500 ring-offset-1"
                        : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleKeyDownForClick}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Glassmorphism
                      colors={["#f87171", "#60a5fa", "#4ade80", "#facc15"]}
                      opacity={0.8}
                      blur={0}
                      className="h-full w-full"
                    />
                    <ColorPicker
                      value={highlightColor}
                      onChange={(newColor) => toggleHighlight(newColor)}
                      isSmall={true}
                      className="opacity-0 absolute inset-0 z-10 cursor-pointer"
                    />
                  </div>
                </div>
                <button
                  className="w-full mt-2 px-2 py-1 text-left rounded text-sm flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeHighlight();
                  }}
                >
                  <FontAwesomeIcon
                    icon={faBan}
                    className="text-gray-500 dark:text-gray-400"
                  />
                  <span>Clear highlight</span>
                </button>
              </div>
            </DropDown>
          </div>

          <div className="w-px h-5 bg-slate-200 dark:bg-white/15"></div>

          {/* Table */}
          <div className="flex items-center gap-0.5">
            <DropDown
              label={
                <div
                  className={`flex h-7 w-7 flex-none items-center justify-center rounded font-semibold leading-[0px] ${
                    getButtonClass(isActive("table"))
                  }`}
                >
                  <FontAwesomeIcon icon={faTable} />
                </div>
              }
              placement="bottom"
              width="w-[480px]"
              className="p-2"
            >
              <div className="grid grid-cols-4 gap-2">
                {/* Table section */}
                <div className="border border-gray-200 dark:border-gray-600 rounded-md p-2 flex flex-col space-y-1">
                  <div className="flex items-center space-x-2 font-medium text-gray-700 dark:text-gray-200 pb-1 border-b border-gray-100 dark:border-gray-600">
                    <FontAwesomeIcon
                      icon={faTable}
                      className="text-gray-500 dark:text-gray-400"
                    />
                    <span>Table</span>
                  </div>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`w-full px-2 py-1 text-left rounded text-sm ${
                          active
                            ? "bg-blue-50 dark:bg-gray-700 text-blue-700 dark:text-blue-400"
                            : "text-gray-700 dark:text-gray-300"
                        }`}
                        onClick={insertTable}
                      >
                        Add table
                      </button>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`w-full px-2 py-1 text-left rounded text-sm ${
                          active
                            ? "bg-blue-50 dark:bg-gray-700 text-blue-700 dark:text-blue-400"
                            : "text-gray-700 dark:text-gray-300"
                        }`}
                        onClick={deleteTable}
                      >
                        Delete table
                      </button>
                    )}
                  </Menu.Item>
                </div>

                {/* Row section */}
                <div className="border border-gray-200 dark:border-gray-600 rounded-md p-2 flex flex-col space-y-1">
                  <div className="flex items-center space-x-2 font-medium text-gray-700 dark:text-gray-200 pb-1 border-b border-gray-100 dark:border-gray-600">
                    <FontAwesomeIcon
                      icon={faTableRows}
                      className="text-gray-500 dark:text-gray-400"
                    />
                    <span>Row</span>
                  </div>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`w-full px-2 py-1 text-left rounded text-sm ${
                          active
                            ? "bg-blue-50 dark:bg-gray-700 text-blue-700 dark:text-blue-400"
                            : "text-gray-700 dark:text-gray-300"
                        }`}
                        onClick={addRowBefore}
                      >
                        Add before
                      </button>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`w-full px-2 py-1 text-left rounded text-sm ${
                          active
                            ? "bg-blue-50 dark:bg-gray-700 text-blue-700 dark:text-blue-400"
                            : "text-gray-700 dark:text-gray-300"
                        }`}
                        onClick={addRowAfter}
                      >
                        Add after
                      </button>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`w-full px-2 py-1 text-left rounded text-sm ${
                          active
                            ? "bg-blue-50 dark:bg-gray-700 text-blue-700 dark:text-blue-400"
                            : "text-gray-700 dark:text-gray-300"
                        }`}
                        onClick={toggleHeaderRow}
                      >
                        Toggle header
                      </button>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`w-full px-2 py-1 text-left rounded text-sm ${
                          active
                            ? "bg-blue-50 dark:bg-gray-700 text-blue-700 dark:text-blue-400"
                            : "text-gray-700 dark:text-gray-300"
                        }`}
                        onClick={deleteRow}
                      >
                        Delete row
                      </button>
                    )}
                  </Menu.Item>
                </div>

                {/* Column section */}
                <div className="border border-gray-200 dark:border-gray-600 rounded-md p-2 flex flex-col space-y-1">
                  <div className="flex items-center space-x-2 font-medium text-gray-700 dark:text-gray-200 pb-1 border-b border-gray-100 dark:border-gray-600">
                    <FontAwesomeIcon
                      icon={faTableColumns}
                      className="text-gray-500 dark:text-gray-400"
                    />
                    <span>Column</span>
                  </div>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`w-full px-2 py-1 text-left rounded text-sm ${
                          active
                            ? "bg-blue-50 dark:bg-gray-700 text-blue-700 dark:text-blue-400"
                            : "text-gray-700 dark:text-gray-300"
                        }`}
                        onClick={addColumnBefore}
                      >
                        Add before
                      </button>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`w-full px-2 py-1 text-left rounded text-sm ${
                          active
                            ? "bg-blue-50 dark:bg-gray-700 text-blue-700 dark:text-blue-400"
                            : "text-gray-700 dark:text-gray-300"
                        }`}
                        onClick={addColumnAfter}
                      >
                        Add after
                      </button>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`w-full px-2 py-1 text-left rounded text-sm ${
                          active
                            ? "bg-blue-50 dark:bg-gray-700 text-blue-700 dark:text-blue-400"
                            : "text-gray-700 dark:text-gray-300"
                        }`}
                        onClick={toggleHeaderColumn}
                      >
                        Toggle header
                      </button>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`w-full px-2 py-1 text-left rounded text-sm ${
                          active
                            ? "bg-blue-50 dark:bg-gray-700 text-blue-700 dark:text-blue-400"
                            : "text-gray-700 dark:text-gray-300"
                        }`}
                        onClick={deleteColumn}
                      >
                        Delete column
                      </button>
                    )}
                  </Menu.Item>
                </div>

                {/* Cell section */}
                <div className="border border-gray-200 dark:border-gray-600 rounded-md p-2 flex flex-col space-y-1">
                  <div className="flex items-center space-x-2 font-medium text-gray-700 dark:text-gray-200 pb-1 border-b border-gray-100 dark:border-gray-600">
                    <FontAwesomeIcon
                      icon={faTableCells}
                      className="text-gray-500 dark:text-gray-400"
                    />
                    <span>Cell</span>
                  </div>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`w-full px-2 py-1 text-left rounded text-sm ${
                          active
                            ? "bg-blue-50 dark:bg-gray-700 text-blue-700 dark:text-blue-400"
                            : "text-gray-700 dark:text-gray-300"
                        }`}
                        onClick={mergeCells}
                      >
                        Merge cells
                      </button>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`w-full px-2 py-1 text-left rounded text-sm ${
                          active
                            ? "bg-blue-50 dark:bg-gray-700 text-blue-700 dark:text-blue-400"
                            : "text-gray-700 dark:text-gray-300"
                        }`}
                        onClick={splitCell}
                      >
                        Split cell
                      </button>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`w-full px-2 py-1 text-left rounded text-sm ${
                          active
                            ? "bg-blue-50 dark:bg-gray-700 text-blue-700 dark:text-blue-400"
                            : "text-gray-700 dark:text-gray-300"
                        }`}
                        onClick={toggleHeaderCell}
                      >
                        Toggle header
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </div>
            </DropDown>
          </div>

          <div className="w-px h-5 bg-slate-200 dark:bg-white/15"></div>

          {/* Image upload */}
          <div className="flex items-center gap-0.5">
            <button
              onClick={addImage}
              className={`flex h-7 w-7 flex-none items-center justify-center rounded font-semibold leading-[0px] bg-transparent ${getToolbarIconClass()}`}
            >
              <FontAwesomeIcon icon={faImage} />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/png,image/jpeg,image/gif"
              onChange={handleFileUpload}
            />
          </div>

          {/* Image Width Input - Show only when image is active */}
          {isImageSelected && (
            <>
              <div className="w-px h-5 bg-slate-200 dark:bg-white/15"></div>
              <div className="flex items-center gap-0.5">
                <label
                  htmlFor="imgWidth"
                  className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap"
                >
                  W:
                </label>
                <FormInput
                  id="imgWidth"
                  label=""
                  className="max-w-20 h-8 p-1 text-sm"
                  type="text"
                  value={currentImageWidth}
                  onChange={(e) => setCurrentImageWidth(e.target.value)}
                  onBlur={() => {
                    const newWidth = currentImageWidth.trim();
                    // Basic validation: allow numbers, px, %
                    if (
                      newWidth === "" ||
                      newWidth.match(/^(\d+(\.?\d+)?(px|%)?|auto)$/i)
                    ) {
                      editor
                        .chain()
                        .focus()
                        .setWidth(newWidth || null)
                        .run(); // Pass null to unset
                    } else {
                      // Reset to original width if input is invalid on blur
                      const originalWidth =
                        editor.getAttributes("resizableImage").width;
                      setCurrentImageWidth(
                        originalWidth ? String(originalWidth) : ""
                      );
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const newWidth = currentImageWidth.trim();
                      if (
                        newWidth === "" ||
                        newWidth.match(/^(\d+(\.?\d+)?(px|%)?|auto)$/i)
                      ) {
                        editor
                          .chain()
                          .focus()
                          .setWidth(newWidth || null)
                          .run();
                        (e.target as HTMLInputElement).blur(); // Remove focus on Enter
                      } else {
                        const originalWidth =
                          editor.getAttributes("resizableImage").width;
                        setCurrentImageWidth(
                          originalWidth ? String(originalWidth) : ""
                        );
                      }
                    }
                  }}
                  placeholder="auto"
                />
              </div>
            </>
          )}

          {/* Link actions */}
          {isActive("link") && (
            <>
              <div className="w-px h-5 bg-slate-200 dark:bg-white/15"></div>
              <div className="flex items-center gap-0.5">
                <button
                  onClick={unsetLink}
                  className={`flex h-7 w-7 flex-none items-center justify-center rounded font-semibold leading-[0px] ${getButtonClass(true)}`}
                >
                  <FontAwesomeIcon icon={faLink} className="rotate-90" />
                </button>
              </div>
            </>
          )}

          {/* Page orientation */}
          {onLandscapeModeChange && (
            <>
              <div className="w-px h-5 bg-slate-200 dark:bg-white/15"></div>
              <div className="flex items-center gap-0.5">
                <button
                  onClick={() => onLandscapeModeChange(false)}
                  className={`flex h-7 w-7 flex-none items-center justify-center rounded font-semibold leading-[0px] ${getButtonClass(!landscapeMode)}`}
                >
                  <FontAwesomeIcon icon={faRectangleVertical} />
                </button>
                <button
                  onClick={() => onLandscapeModeChange(true)}
                  className={`flex h-7 w-7 flex-none items-center justify-center rounded font-semibold leading-[0px] ${getButtonClass(landscapeMode)}`}
                >
                  <FontAwesomeIcon icon={faRectangleWide} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentRichTextToolbar;
